import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import PageTransition from "./components/PageTransition";
import CanonicalTag from "./components/CanonicalTag";
import Index from "./pages/Index";

// Lazy load non-critical route components for code splitting
const AllProjects = lazy(() => import("./pages/AllProjects"));
const LocalSeoProject = lazy(() => import("./pages/LocalSeoProject"));
const EcommerceSeoProject = lazy(() => import("./pages/EcommerceSeoProject"));
const TechnicalSeoProject = lazy(() => import("./pages/TechnicalSeoProject"));
const ContentSeoProject = lazy(() => import("./pages/ContentSeoProject"));
const InternationalSeoProject = lazy(() => import("./pages/InternationalSeoProject"));
const SeoAnalyticsProject = lazy(() => import("./pages/SeoAnalyticsProject"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Scroll to top on route change
const ScrollToTopOnNavigate = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  
  return (
    <>
      <ScrollToTopOnNavigate />
      <CanonicalTag />
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <PageTransition key={location.pathname}>
          <Routes location={location}>
            <Route path="/" element={<Index />} />
            <Route path="/projects" element={<AllProjects />} />
            <Route path="/projects/local-seo" element={<LocalSeoProject />} />
            <Route path="/projects/ecommerce-seo" element={<EcommerceSeoProject />} />
            <Route path="/projects/technical-seo" element={<TechnicalSeoProject />} />
            <Route path="/projects/content-seo" element={<ContentSeoProject />} />
            <Route path="/projects/international-seo" element={<InternationalSeoProject />} />
            <Route path="/projects/seo-analytics" element={<SeoAnalyticsProject />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
